import { Link } from 'react-router-dom';
import { DocumentLinks } from '../components/DocumentLinks';
import { useSiteContent } from '../hooks/useSiteContent';
import { useLanguage } from '../i18n/LanguageContext';
import { mediaUrl } from '../services/api';
import { ContentBlock } from '../types/content';
import bannerImage from '../components/assets/1.jpg';
import overviewImage from '../components/assets/2.jpg';
import customsImage from '../components/assets/522.png';
import serviceImageB from '../components/assets/11.jpg';
import iconArea from '../components/assets/icon-area.png';
import iconDepth from '../components/assets/icon-depth.png';
import iconWarehouse from '../components/assets/icon-warehouse.png';
import iconContainers from '../components/assets/icon-containers.png';
import iconReefer from '../components/assets/icon-reefer.png';
import { DEFAULT_SERVICES } from '../data/defaults';

const PAGE_SLUG = 'liman-ozellikleri';

const SERVICE_PHOTOS: { match: string; image: string }[] = [
  { match: 'konteyner', image: serviceImageB },
  { match: 'dokme', image: overviewImage },
  { match: 'genel kargo', image: overviewImage },
  { match: 'cfs', image: overviewImage },
  { match: 'proje', image: customsImage },
  { match: 'antrepo', image: customsImage },
  { match: 'depo', image: customsImage },
  { match: 'silo', image: customsImage },
  { match: 'atik', image: serviceImageB },
];

function normalizeServiceTitle(title: string) {
  return title
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ç/g, 'c')
    .replace(/ğ/g, 'g');
}

function serviceFallbackPhoto(title: string) {
  const normalized = normalizeServiceTitle(title);
  const matched = SERVICE_PHOTOS.find((item) => normalized.includes(item.match))?.image;
  if (matched) return matched;
  const fromDefaults = DEFAULT_SERVICES.find((item) =>
    normalizeServiceTitle(item.title).includes(normalized.split(' ')[0] || ''),
  )?.imageUrl;
  return fromDefaults || overviewImage;
}

function normalizeLookup(value: string) {
  return value
    .toLocaleLowerCase('tr-TR')
    .replace(/ı/g, 'i')
    .replace(/İ/g, 'i')
    .replace(/ö/g, 'o')
    .replace(/ü/g, 'u')
    .replace(/ş/g, 's')
    .replace(/ç/g, 'c')
    .replace(/ğ/g, 'g');
}

function normalizeStatLabel(value?: string) {
  return normalizeLookup(value || '');
}

function nlToBr(value?: string) {
  return (value || '')
    .replace(/\\n/g, '\n')
    .replace(/\r?\n/g, '<br />');
}

function cleanInlineText(value?: string) {
  return (value || '')
    .replace(/\\n/g, ' ')
    .replace(/\r?\n/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function splitMultilineText(value?: string) {
  return (value || '')
    .replace(/\\n/g, '\n')
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);
}

function formatBerthTitle(value?: string) {
  const text = (value || '').trim();
  if (!text) return '';
  if (/[<>]/.test(text)) return nlToBr(text);

  const parts = text.split(/\s+/);
  if (parts.length <= 1) return `<span>${text}</span>`;

  return `<span>${parts[0]}</span> ${parts.slice(1).join(' ')}`.replace(/\r?\n/g, '<br />');
}

function formatEquipmentTitle(value?: string) {
  const text = (value || '').trim();
  if (!text) return '';
  if (/[<>]/.test(text)) return nlToBr(text);

  const parts = text.split(/\s+/);
  if (parts.length <= 1) return `<span>${text}</span>`;

  return `${parts.slice(0, -1).join(' ')} <span>${parts[parts.length - 1]}</span>`;
}

function formatCustomsTitle(value?: string) {
  const text = cleanInlineText(value);
  if (!text) return '';
  if (/[<>]/.test(text)) return nlToBr(text);

  const parts = text.split(/\s+/);
  if (parts.length <= 1) return `<span>${text}</span>`;

  return `<span>${parts[0]}</span> ${parts.slice(1).join(' ')}`;
}

const PORT_METRIC_ICONS = [iconArea, iconDepth, iconWarehouse, iconContainers, iconReefer];

const DEFAULT_OVERVIEW = {
  eyebrow: 'GENEL GÖRÜNÜM',
  title: 'Tek lokasyonda <span>1.300.000 m²</span>',
  description:
    "Yumurtalık'ta, Doğu Akdeniz'in en stratejik noktasında konumlanan TBH Port Adana; konteyner, dökme yük, antrepo, milli depo ve büyüme rezervi alanlarını tek çatı altında entegre eder.",
  pills: ['-18 M İSKELE DERİNLİĞİ', 'GÜMRÜKLÜ DEPOLAMA', 'GENİŞ GEÇİCİ DEPOLAMA ALANI'],
  listLeft: [
    '15.000 m² Milli Depolama Alanı',
    '1.297 m İskele Uzunluğu',
    '-18 m’ye Kadar İskele Derinliği',
    '12.000 Ton Silo',
  ],
  listRight: [
    '30.000 m² Kapalı A Tipi Antrepo',
    '44.000 m² Açık A Tipi Antrepo',
    '34.000 m² Petrokok İçin A Tipi Antrepo',
    '250.000 m² GDA Alanı',
  ],
};

const DEFAULT_BERTH = {
  eyebrow: 'İSKELE',
  title: '<span>1.297</span> uzunluk, çoklu iskele',
  description: 'Farklı derinliklerde 5 iskele, geniş yelpazede gemi tipini güvenle karşılar.',
  cards: [
    {
      eyebrow: 'İSKELE 1,2',
      title: 'Derin İskeleler',
      image: bannerImage,
      rows: [
        ['Derinlik', '-18m / -17m'],
        ['Uzunluk', '305m / 251m'],
        ['Genişlik', '28m'],
        ['Konum', 'Çoklu İskele'],
      ] as [string, string][],
    },
    {
      eyebrow: 'İSKELE 3,4,5',
      title: 'Uzun Hat İskeleleri',
      image: overviewImage,
      rows: [
        ['Derinlik', '-9m / -16m'],
        ['Uzunluk', '741m'],
        ['Genişlik', '20m'],
        ['Konum', 'Yan İskele'],
      ] as [string, string][],
    },
  ],
};

const DEFAULT_EQUIPMENT = {
  eyebrow: 'LİMAN EKİPMANLARI',
  title: 'Verimli ve çevre dostu <span>ekipman</span> parkı',
  description:
    'Operasyonel verimliliği maksimize eden, modern ve çevre dostu liman ekipmanlarımızla her tipte yüke hızlı tepki veriyoruz.',
  items: [
    {
      count: 3,
      title: 'MHC - Mobile Harbour Crane',
      description: '2 × LHM 550 (18. sıra) + 1 × LHM 420 (16. sıra). Çok amaçlı operasyonlar.',
    },
    {
      count: 2,
      title: 'MHC - Sennebogen',
      description: 'Genel kargo ve dökme yük operasyonları için özel elleçleme sahil vinci.',
    },
    {
      count: 4,
      title: 'Reach Stacker',
      description: 'Konteyner istifleme ve transfer için yüksek manevra kabiliyetli.',
    },
    {
      count: 1,
      title: 'Empty Container Handler',
      description: 'Boş konteynerlerin hızlı & güvenli istiflenmesi.',
    },
    {
      count: 1,
      title: 'Forklift 33 Ton',
      description: 'Ağır tonajlı paletli yük ve proje yük transferi için yüksek kapasiteli forklift.',
    },
    {
      count: 11,
      title: 'Forklift Filosu',
      description: 'Çeşitli tonajlarda 11 adet forklift, antrepo & saha içi taşıma.',
    },
    {
      count: 7,
      title: 'Excavator + Loader',
      description: 'Dökme yük operasyonları için 7 ekskavatör + 7 loader.',
    },
    {
      count: 2,
      title: 'Telehandler + Mini Loader',
      description: 'Hassas ve dar alan operasyonları için yardımcı ekipman.',
    },
  ],
};

const DEFAULT_CUSTOMS = {
  eyebrow: 'GÜMRÜK & DİJİTAL TAKİP',
  title: '<span>Gölovası</span> Gümrük<br />Müdürlüğü',
  description:
    'TBH Port Adana sahasında Gölovası Gümrük Müdürlüğü kurulmuştur. Bu gelişme ile gümrük işlemlerinin daha hızlı, etkin ve yerinde yürütülmesi hedeflenmektedir.',
  pills: ['KLTS (KONTEYNER LİMAN TAKİP SİSTEMİ)', 'DİJİTALLEŞME'],
  features: [
    'Liman sahası içinde Gölovası Gümrük Müdürlüğü',
    'KLTS – Konteyner Liman Takip Sistemi',
    'Gümrük işlemlerinde dijitalleşme & izlenebilirlik',
    'Yerinde, hızlı, etkin gümrükleme',
    'Operasyonel verimlilikte artış',
  ],
  badge: 'Gölovası Gümrük Müdürlüğü',
};

const DEFAULT_XRAY = {
  eyebrow: 'KONTEYNER ALTYAPISI',
  title: '<span>X-Ray &amp; Reefer</span> altyapısı',
  description:
    'X-Ray tarama tesisimiz hızlı ve güvenli tarama için gelişmiş güvenlik teknolojileriyle donatılmış olup saatte 20 konteyner işleme kapasitesine sahiptir. Soğutmalı yükler için güvenilir çözümler sunan reefer plug altyapımız soğuk zincir taşımacılığında güvenli teslimatları sağlar.',
  pills: ['X-RAY 20 KONT./SAAT', '200 REEFER PLUG'],
  features: [
    'X-Ray: hızlı & güvenli konteyner taraması',
    'Saatte 20 konteyner işleme kapasitesi',
    '40 sabit + 160 ek reefer plug ünitesi',
    'Soğuk zincir taşımacılığında güvenli teslimat',
    'Gelişmiş güvenlik teknolojileri',
  ],
  badge: 'MS SPEKTRAL X-RAY',
};

function blockText(block: ContentBlock | undefined, fallback: string) {
  return block?.content || fallback;
}

// Servisten isaretlemesiz baslik gelirse sayidan itibaren kirmizi vurgulanir.
function formatOverviewTitle(value: string) {
  if (value.includes('<')) return value;
  return value.replace(/(\d.*)$/, '<span>$1</span>');
}

// Liste bloklarinda guncel/cevrilmis metin Aciklama'dadir (portal her kayitta Aciklama ile
// Ekstra.items'i birlikte yazar; dogrudan cevrilen EN kayitlarinda items eski TR kopyadir).
function blockItems(block: ContentBlock | undefined, fallback: string[]) {
  const fromContent = splitMultilineText(block?.content);
  if (fromContent.length > 0) return fromContent;
  if (Array.isArray(block?.items) && block!.items!.length > 0) {
    return block!.items!.map((item) => String(item));
  }
  return fallback;
}

function parseRowItems(items: string[]): [string, string][] {
  return items
    .map((item) => {
      const [label, ...rest] = String(item).split('|');
      const value = rest.join('|').trim();
      if (!label?.trim() || !value) return null;
      return [label.trim(), value] as [string, string];
    })
    .filter((row): row is [string, string] => Boolean(row));
}

function parseRowTextBlock(value?: string): [string, string][] {
  const lines = (value || '')
    .replace(/\\n/g, '\n')
    .split(/\r?\n/)
    .map((item) => item.trim())
    .filter(Boolean);

  const piped = parseRowItems(lines);
  if (piped.length > 0) return piped;

  // "Derinlik: -18 m" bicimi
  const colon = lines
    .map((line) => {
      const match = line.match(/^([^:]+):\s*(.+)$/);
      return match ? ([match[1].trim(), match[2].trim()] as [string, string]) : null;
    })
    .filter((row): row is [string, string] => Boolean(row));
  if (colon.length > 0 && colon.length === lines.length) return colon;

  const rows: [string, string][] = [];
  for (let index = 0; index < lines.length - 1; index += 2) {
    rows.push([lines[index], lines[index + 1]]);
  }
  return rows;
}

function parseEquipmentItems(items: string[]) {
  return items
    .map((item) => {
      const [countRaw, title, ...rest] = String(item).split('|');
      const description = rest.join('|').trim();
      const count = Number(countRaw);
      if (!title?.trim() || !description) return null;
      return {
        count: Number.isFinite(count) ? count : 0,
        title: title.trim(),
        description,
      };
    })
    .filter((item): item is { count: number; title: string; description: string } => Boolean(item));
}

function berthCardFromBlock(
  block: ContentBlock | undefined,
  fallback: (typeof DEFAULT_BERTH.cards)[number],
) {
  const fromContent = parseRowTextBlock(block?.content);
  const rows =
    fromContent.length > 0
      ? fromContent
      : parseRowItems(blockItems(block, fallback.rows.map(([l, v]) => `${l}|${v}`)));
  return {
    eyebrow: block?.subtitle || fallback.eyebrow,
    title: block?.title || fallback.title,
    image: mediaUrl(block?.imageUrl) || fallback.image,
    rows: rows.length > 0 ? rows : fallback.rows,
  };
}

export function PortFeaturesPage() {
  const { content, getPage, getBlock, getStats } = useSiteContent();
  const { t, tList } = useLanguage();
  const page = getPage(PAGE_SLUG);

  const findHeroBlock = (...patterns: string[]) =>
    content.contentBlocks.find((block) => {
      const haystack = normalizeLookup(
        `${block.pageSlug || ''} ${block.blockKey || ''} ${block.title || ''} ${block.content || ''}`,
      );
      return patterns.some((pattern) => haystack.includes(normalizeLookup(pattern)));
    });

  const heroEyebrow =
    getBlock(PAGE_SLUG, 'infrastructure_hero_eyebrow') ||
    findHeroBlock(
      'Liman Ozellikleri Hero - Ust Etiket',
      'Liman Özellikleri Hero - Ust Etiket',
      'Liman Ozellikleri Hero - Ust Yazi',
      'Liman Özellikleri Hero - Ust Yazi',
      'infrastructure_hero_eyebrow',
      'Hizmetler Hero - Ust Yazi',
      'Hizmetler Hero - Ust Etiket',
      'services_hero_eyebrow',
    );
  const heroTitle =
    getBlock(PAGE_SLUG, 'infrastructure_hero_title') ||
    findHeroBlock(
      'Liman Ozellikleri Hero - Baslik',
      'Liman Özellikleri Hero - Baslik',
      'infrastructure_hero_title',
      'Hizmetler Hero - Ana Baslik',
      'Hizmetler Hero - Baslik',
      'services_hero_title',
    );
  const heroDescription =
    getBlock(PAGE_SLUG, 'infrastructure_hero_description') ||
    findHeroBlock(
      'Liman Ozellikleri Hero - Aciklama',
      'Liman Özellikleri Hero - Aciklama',
      'infrastructure_hero_description',
      'Hizmetler Hero - Aciklama',
      'services_hero_description',
    );
  // Hero gorseli bu sayfanin kendi kaydindan gelir (portal Liman Ozellikleri > Hero gorseli).
  const heroImageBlock =
    getBlock(PAGE_SLUG, 'infrastructure_hero_image') ||
    findHeroBlock(
      'Liman Ozellikleri Hero - Gorsel URL',
      'Liman Özellikleri Hero - Gorsel URL',
      'infrastructure_hero_image',
    );
  const heroCtaTextBlock = getBlock(PAGE_SLUG, 'infrastructure_hero_cta_text') || findHeroBlock(
    'Liman Ozellikleri Hero - Buton Metni',
    'Liman Özellikleri Hero - Buton Metni',
    'infrastructure_hero_cta_text',
    'Hizmetler Hero - Buton Metni',
    'services_hero_cta_text',
  );
  const heroCtaLinkBlock = getBlock(PAGE_SLUG, 'infrastructure_hero_cta_link') || findHeroBlock(
    'Liman Ozellikleri Hero - Buton Linki',
    'Liman Özellikleri Hero - Buton Linki',
    'infrastructure_hero_cta_link',
    'Hizmetler Hero - Buton Linki',
    'services_hero_cta_link',
  );
  const overviewEyebrow =
    getBlock(PAGE_SLUG, 'infrastructure_overview_eyebrow') ||
    findHeroBlock(
      'Genel Gorunum - Ust Etiket',
      'Genel Görünüm - Ust Etiket',
      'infrastructure_overview_eyebrow',
    );
  const overviewTitle =
    getBlock(PAGE_SLUG, 'infrastructure_overview_title') ||
    findHeroBlock(
      'Genel Gorunum - Baslik',
      'Genel Görünüm - Baslik',
      'infrastructure_overview_title',
    );
  const overviewDescription =
    getBlock(PAGE_SLUG, 'infrastructure_overview_description') ||
    findHeroBlock(
      'Genel Gorunum - Aciklama',
      'Genel Görünüm - Aciklama',
      'infrastructure_overview_description',
    );
  const overviewPills = blockItems(
    getBlock(PAGE_SLUG, 'infrastructure_overview_pills') ||
      findHeroBlock(
        'Genel Gorunum - Pillar',
        'Genel Görünüm - Pillar',
        'infrastructure_overview_pills',
      ),
    DEFAULT_OVERVIEW.pills,
  );
  const overviewLeft = blockItems(
    getBlock(PAGE_SLUG, 'infrastructure_overview_list_left') ||
      findHeroBlock(
        'Genel Gorunum - Sol Liste',
        'Genel Görünüm - Sol Liste',
        'infrastructure_overview_list_left',
      ),
    DEFAULT_OVERVIEW.listLeft,
  );
  const overviewRight = blockItems(
    getBlock(PAGE_SLUG, 'infrastructure_overview_list_right') ||
      findHeroBlock(
        'Genel Gorunum - Sag Liste',
        'Genel Görünüm - Sag Liste',
        'Genel Gorunum - Sağ Liste',
        'Genel Görünüm - Sağ Liste',
        'infrastructure_overview_list_right',
      ),
    DEFAULT_OVERVIEW.listRight,
  );
  const overviewImageSrc =
    mediaUrl(
      (
        getBlock(PAGE_SLUG, 'infrastructure_overview_image') ||
        findHeroBlock(
          'Genel Gorunum - Gorsel',
          'Genel Görünüm - Gorsel',
          'Genel Gorunum - Görsel',
          'Genel Görünüm - Görsel',
          'infrastructure_overview_image',
        )
      )?.content,
    ) || overviewImage;

  const berthEyebrow =
    getBlock(PAGE_SLUG, 'infrastructure_berth_eyebrow') ||
    findHeroBlock('Iskele - Ust Etiket', 'İskele - Ust Etiket', 'infrastructure_berth_eyebrow');
  const berthTitle =
    getBlock(PAGE_SLUG, 'infrastructure_berth_title') ||
    findHeroBlock('Iskele - Baslik', 'İskele - Baslik', 'infrastructure_berth_title');
  const berthDescription =
    getBlock(PAGE_SLUG, 'infrastructure_berth_description') ||
    findHeroBlock('Iskele - Aciklama', 'İskele - Aciklama', 'infrastructure_berth_description');
  const berthCardBlock1 =
    getBlock(PAGE_SLUG, 'infrastructure_berth_card_1') ||
    findHeroBlock('01 KART - Derin Iskeleler', '01 KART - Derin İskeleler', 'infrastructure_berth_card_1');
  const berthCardBlock2 =
    getBlock(PAGE_SLUG, 'infrastructure_berth_card_2') ||
    findHeroBlock('02 KART - Uzun Hat Iskeleleri', '02 KART - Uzun Hat İskeleleri', 'infrastructure_berth_card_2');
  const berthCards = [
    {
      ...berthCardFromBlock(berthCardBlock1, DEFAULT_BERTH.cards[0]),
      title: berthCardBlock1?.title?.replace(/^01\s*kart\s*-\s*/i, '') || DEFAULT_BERTH.cards[0].title,
    },
    {
      ...berthCardFromBlock(berthCardBlock2, DEFAULT_BERTH.cards[1]),
      title: berthCardBlock2?.title?.replace(/^02\s*kart\s*-\s*/i, '') || DEFAULT_BERTH.cards[1].title,
    },
  ];

  const equipmentEyebrow =
    getBlock(PAGE_SLUG, 'infrastructure_equipment_eyebrow') ||
    findHeroBlock('Ekipman - Ust Etiket', 'infrastructure_equipment_eyebrow');
  const equipmentTitle =
    getBlock(PAGE_SLUG, 'infrastructure_equipment_title') ||
    findHeroBlock('Ekipman - Baslik', 'infrastructure_equipment_title');
  const equipmentDescription =
    getBlock(PAGE_SLUG, 'infrastructure_equipment_description') ||
    findHeroBlock('Ekipman - Aciklama', 'infrastructure_equipment_description');
  const equipmentItemsRaw = parseEquipmentItems(
    blockItems(
      getBlock(PAGE_SLUG, 'infrastructure_equipment_items') ||
        findHeroBlock('Ekipman - Liste', 'infrastructure_equipment_items'),
      DEFAULT_EQUIPMENT.items.map((item) => `${item.count}|${item.title}|${item.description}`),
    ),
  );
  const equipmentItems = equipmentItemsRaw.length > 0 ? equipmentItemsRaw : DEFAULT_EQUIPMENT.items;

  const customsEyebrow =
    getBlock(PAGE_SLUG, 'infrastructure_customs_eyebrow') ||
    findHeroBlock('Gumruk - Ust Etiket', 'Gümrük - Ust Etiket', 'infrastructure_customs_eyebrow');
  const customsTitle =
    getBlock(PAGE_SLUG, 'infrastructure_customs_title') ||
    findHeroBlock('Gumruk - Baslik', 'Gümrük - Baslik', 'infrastructure_customs_title');
  const customsDescription =
    getBlock(PAGE_SLUG, 'infrastructure_customs_description') ||
    findHeroBlock('Gumruk - Aciklama', 'Gümrük - Aciklama', 'infrastructure_customs_description');
  const customsPillsBlock =
    getBlock(PAGE_SLUG, 'infrastructure_customs_pills') ||
    findHeroBlock('Gumruk - Pillar', 'Gümrük - Pillar', 'infrastructure_customs_pills');
  const customsFeaturesBlock =
    getBlock(PAGE_SLUG, 'infrastructure_customs_features') ||
    findHeroBlock('Gumruk - Ozellikler', 'Gümrük - Ozellikler', 'Gümrük - Özellikler', 'infrastructure_customs_features');
  const customsPills = splitMultilineText(customsPillsBlock?.content);
  const customsFeatures = splitMultilineText(customsFeaturesBlock?.content);
  const customsImg =
    mediaUrl(
      (
        getBlock(PAGE_SLUG, 'infrastructure_customs_image') ||
        findHeroBlock('Gumruk - Gorsel', 'Gümrük - Gorsel', 'Gümrük - Görsel', 'infrastructure_customs_image')
      )?.content,
    ) || customsImage;
  const customsBadge = cleanInlineText(
    blockText(
      getBlock(PAGE_SLUG, 'infrastructure_customs_badge') ||
        findHeroBlock('Gumruk - Rozet', 'Gümrük - Rozet', 'infrastructure_customs_badge'),
      DEFAULT_CUSTOMS.badge,
    ),
  );

  const xrayEyebrow = getBlock(PAGE_SLUG, 'infrastructure_xray_eyebrow');
  const xrayTitle = getBlock(PAGE_SLUG, 'infrastructure_xray_title');
  const xrayDescription = getBlock(PAGE_SLUG, 'infrastructure_xray_description');
  const xrayPills = blockItems(getBlock(PAGE_SLUG, 'infrastructure_xray_pills'), DEFAULT_XRAY.pills);
  const xrayFeatures = blockItems(
    getBlock(PAGE_SLUG, 'infrastructure_xray_features'),
    DEFAULT_XRAY.features,
  );
  const xrayImg = mediaUrl(getBlock(PAGE_SLUG, 'infrastructure_xray_image')?.content) || customsImage;
  const xrayBadge = blockText(getBlock(PAGE_SLUG, 'infrastructure_xray_badge'), DEFAULT_XRAY.badge);

  const heroStats = getStats('hero');
  const infrastructureStatOrder = [
    'liman alani',
    'derinlik',
    'kapali depolama alani',
    'yillik konteyner kapasitesi',
    'reefer plug',
  ];
  const infrastructureStatsMap = new Map<string, (typeof content.statistics)[number]>();

  content.statistics.forEach((item) => {
    const label = normalizeStatLabel(item.label);
    const matchedKey = infrastructureStatOrder.find((key) => label === key);
    if (!matchedKey) return;

    const current = infrastructureStatsMap.get(matchedKey);
    if (!current || (item.id ?? 0) > (current.id ?? 0)) {
      infrastructureStatsMap.set(matchedKey, item);
    }
  });

  // Portal Liman Ozellikleri > Ust Istatistikler "altyapi" kategorisinde, sirayla kaydedilir.
  // Etiket dile gore degistigi icin once kategori + sira kullanilir; Turkce etiket eslesmesi
  // sadece eski kayitlar icin yedektir.
  const altyapiStats = content.statistics
    .filter((item) => item.category === 'altyapi')
    .sort((a, b) => a.sortOrder - b.sortOrder);
  const orderedInfrastructureStats = altyapiStats.length
    ? altyapiStats
    : infrastructureStatOrder
        .map((key) => infrastructureStatsMap.get(key))
        .filter((item): item is NonNullable<typeof item> => Boolean(item));
  const highlightStats = [
    (orderedInfrastructureStats.length ? orderedInfrastructureStats : heroStats).find((item) =>
      item.value.toLocaleLowerCase('tr-TR').includes('teu'),
    ),
    (orderedInfrastructureStats.length ? orderedInfrastructureStats : heroStats).find((item) =>
      item.value.toLocaleLowerCase('tr-TR').includes('m²'),
    ),
  ].filter((item): item is NonNullable<typeof item> => Boolean(item));

  const metrics = orderedInfrastructureStats.map((item, index) => ({
    // Portal bu kartlarda degeri Aciklama'ya da yazar; ceviri (orn. "200 Units") orada tutulur
    value: (item.category === 'altyapi' && item.description) || item.value,
    label: item.label,
    icon: mediaUrl(item.imageUrl) || PORT_METRIC_ICONS[index % PORT_METRIC_ICONS.length],
  }));

  const heroImage =
    mediaUrl(heroImageBlock?.content || heroImageBlock?.imageUrl) ||
    mediaUrl(page?.heroImage) ||
    bannerImage;

  return (
    <>
      <section
        className="port-features-hero"
        style={{ backgroundImage: `url(${heroImage})` }}
      >
        <div className="port-features-hero-overlay" />
        <div className="container port-features-hero-inner">
          <div className="eyebrow port-features-hero-eyebrow">
            {t(heroEyebrow?.content || 'LIMAN ÖZELLİKLERİ')}
          </div>
          <h1
            className="port-features-hero-title"
            dangerouslySetInnerHTML={{
              __html: t(
                heroTitle?.content ||
                  page?.heroTitle ||
                  "Doğu Akdeniz'in <em>stratejik</em><br />liman altyapısı",
              ),
            }}
          />
          <p className="port-features-hero-description">
            {t(
              heroDescription?.content ||
                page?.heroSubtitle ||
                '1.297 metre iskele uzunluğu, çoklu iskelelerde -18 m derinlik, A Tipi gümrüklü antrepolar ve modern ekipman parkımızla her ölçekte yüke entegre çözüm sunuyoruz.',
            )}
          </p>
          <div className="port-features-hero-stats">
            {highlightStats.map((item) => (
              <div key={item.id}>
                <b>{item.value}</b>
                <span>{t(item.label)}</span>
              </div>
            ))}
          </div>
          {(() => {
            const ctaText = t(heroCtaTextBlock?.content || heroCtaTextBlock?.title || 'TEKLİF AL');
            const ctaLink = heroCtaLinkBlock?.content || heroCtaLinkBlock?.title || '/iletisim';
            const isExternal = /^(https?:)?\/\//i.test(ctaLink);

            if (isExternal) {
              return (
                <a
                  href={ctaLink}
                  className="btn btn-red port-features-hero-button"
                  target="_blank"
                  rel="noreferrer"
                >
                  {ctaText}
                </a>
              );
            }

            return (
              <Link to={ctaLink} className="btn btn-red port-features-hero-button">
                {ctaText}
              </Link>
            );
          })()}
        </div>
      </section>

      {metrics.length > 0 && (
        <section className="port-metrics-strip">
          <div className="port-metrics-grid">
            {metrics.map((item) => (
              <div className="port-metric-card" key={`${item.label}-${item.value}`}>
                <div className="port-metric-icon">
                  <img src={item.icon} alt="" />
                </div>
                <strong>{item.value}</strong>
                <span>{t(item.label)}</span>
              </div>
            ))}
          </div>
        </section>
      )}

      <section className="port-overview-section">
        <div className="container">
          <div className="port-overview-eyebrow">
            {t(blockText(overviewEyebrow, DEFAULT_OVERVIEW.eyebrow))}
          </div>
          <h2
            className="port-overview-title"
            dangerouslySetInnerHTML={{
              __html: formatOverviewTitle(t(blockText(overviewTitle, DEFAULT_OVERVIEW.title))),
            }}
          />

          <div className="port-overview-pills">
            {tList(overviewPills).map((item) => (
              <span key={item}>{item}</span>
            ))}
          </div>

          <p className="port-overview-description">
            {t(blockText(overviewDescription, DEFAULT_OVERVIEW.description))}
          </p>

          <div className="port-overview-list-grid">
            <ul>
              {tList(overviewLeft).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
            <ul>
              {tList(overviewRight).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>

          <div className="port-overview-image-wrap">
            <img
              src={overviewImageSrc}
              alt={t('TBH Port Adana genel görünüm')}
              className="port-overview-image"
            />
          </div>
        </div>
      </section>

      <section className="berth-section">
        <div className="container">
          <div className="berth-eyebrow">{t(blockText(berthEyebrow, DEFAULT_BERTH.eyebrow))}</div>
          <h2
            className="berth-title"
            dangerouslySetInnerHTML={{
              __html: formatBerthTitle(t(blockText(berthTitle, DEFAULT_BERTH.title))),
            }}
          />
          <p className="berth-description">
            {t(blockText(berthDescription, DEFAULT_BERTH.description))}
          </p>

          <div className="berth-grid">
            {berthCards.map((card) => (
              <article
                key={card.title}
                className="berth-card"
                style={{ backgroundImage: `url(${card.image})` }}
              >
                <div className="berth-card-overlay" />
                <div className="berth-card-content">
                  <div className="berth-card-eyebrow">{t(card.eyebrow)}</div>
                  <h3 style={{ whiteSpace: 'pre-line' }}>{t(card.title)}</h3>
                  <div className="berth-card-table">
                    {card.rows.map(([label, value]) => (
                      <div className="berth-card-row" key={label}>
                        {/* "Konum" iskele tablosunda "Configuration" anlamindadir */}
                        <span>{t(label, 'iskele')}</span>
                        <strong>{t(value)}</strong>
                      </div>
                    ))}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="equipment-section">
        <div className="container">
          <div className="equipment-eyebrow">
            {t(blockText(equipmentEyebrow, DEFAULT_EQUIPMENT.eyebrow))}
          </div>
          <h2
            className="equipment-title"
            dangerouslySetInnerHTML={{
              __html: formatEquipmentTitle(t(blockText(equipmentTitle, DEFAULT_EQUIPMENT.title))),
            }}
          />
          <p className="equipment-description">
            {t(blockText(equipmentDescription, DEFAULT_EQUIPMENT.description))}
          </p>

          <div className="equipment-board">
            {equipmentItems.map((item) => (
              <article className="equipment-card" key={item.title}>
                <div className="equipment-count">
                  <small>x</small>
                  <strong>{item.count}</strong>
                </div>
                <h3>{t(item.title)}</h3>
                <p>{t(item.description)}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="customs-section">
        <div className="container customs-layout">
          <div className="customs-copy">
            <div className="customs-eyebrow">
              {t(cleanInlineText(blockText(customsEyebrow, DEFAULT_CUSTOMS.eyebrow)))}
            </div>
            <h2
              className="customs-title"
              dangerouslySetInnerHTML={{
                __html: formatCustomsTitle(t(blockText(customsTitle, DEFAULT_CUSTOMS.title))),
              }}
            />
            <div className="customs-pills">
              {tList(customsPills.length > 0 ? customsPills : DEFAULT_CUSTOMS.pills).map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
            <p className="customs-description">
              {t(cleanInlineText(blockText(customsDescription, DEFAULT_CUSTOMS.description)))}
            </p>
            <ul className="customs-list">
              {tList(
                customsFeatures.length > 0 ? customsFeatures : DEFAULT_CUSTOMS.features,
              ).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>

          <div className="customs-image-wrap">
            <img src={customsImg} alt={t(customsBadge)} className="customs-image" />
            <div className="customs-image-badge">{t(customsBadge)}</div>
          </div>
        </div>
      </section>

      <section className="xray-section">
        <div className="container xray-layout">
          <div className="xray-image-wrap">
            <img src={xrayImg} alt={t(xrayBadge)} className="xray-image" />
            <div className="xray-image-badge">{t(xrayBadge)}</div>
          </div>

          <div className="xray-copy">
            <div className="xray-eyebrow">{t(blockText(xrayEyebrow, DEFAULT_XRAY.eyebrow))}</div>
            <h2
              className="xray-title"
              dangerouslySetInnerHTML={{ __html: t(blockText(xrayTitle, DEFAULT_XRAY.title)) }}
            />
            <div className="xray-pills">
              {tList(xrayPills).map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
            <p className="xray-description">
              {t(blockText(xrayDescription, DEFAULT_XRAY.description))}
            </p>
            <ul className="xray-list">
              {tList(xrayFeatures).map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ul>
          </div>
        </div>
      </section>
    </>
  );
}
